<template>
    <div
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <q-stepper v-model="step" class="q-pa-md" vertical animated header-nav>
            <banner
                class="q-mb-md"
                :is-visible="isLegacyReport"
                type="info"
                :message="legacyReportMessage"
            />
            <q-form @submit="isEdit ? update() : create()" ref="eventForm">
                <!-- Report type fields -->
                <q-step
                    :name="1"
                    :title="
                        isEdit
                            ? $t('Edit an automatic report')
                            : $t('Create an automatic report')
                    "
                    icon="fas fa-calendar-clock"
                    :done="validateStep(1)"
                >
                    <q-select
                        id="type-select"
                        v-model="form.report.type"
                        @update:model-value="resetFields()"
                        :options="typeOptions"
                        :label="$t('Type of Report')"
                        :rules="[val => validateField(val)]"
                        class="q-mt-sm"
                        color="accent"
                        lazy-rules
                        map-options
                        emit-value
                        outlined
                    />
                    <!-- Phases report additional options -->
                    <div
                        v-if="form.report.type === 'phases'"
                        class="col-12 q-px-xs q-pb-md"
                    >
                        <!-- Only downtime and switch events -->
                        <q-checkbox
                            v-model="form.options.downtime_only"
                            :label="
                                $t('Include downtime and switch events only')
                            "
                            class="row"
                            color="accent"
                        />
                    </div>
                    <!-- Trends report additional options -->
                    <div
                        v-if="form.report.type === 'trends'"
                        class="col-12 q-px-xs q-pb-md"
                    >
                        <!-- group the trends report data by product. This is default option -->
                        <q-checkbox
                            :model-value="
                                form.options.group_production_by_product
                            "
                            :label="$t('Group production by product')"
                            class="row"
                            color="accent"
                            @update:model-value="onTrendsGroupByProductToggle"
                        />
                        <!-- group the trends report data by resolution.-->
                        <q-checkbox
                            :model-value="
                                form.options.group_production_by_resolution
                            "
                            :label="
                                $t(
                                    'Group production totals by selected resolution'
                                )
                            "
                            class="row"
                            color="accent"
                            @update:model-value="
                                onTrendsGroupByResolutionToggle
                            "
                        />
                    </div>
                    <!-- Non-benchmark report -->
                    <q-select
                        v-if="form.report.type !== 'benchmark'"
                        id="facility-select"
                        :model-value="form.selectedFacility"
                        @update:model-value="updateSelectedFacility"
                        :options="facilityOptions"
                        :label="$t('Select a Facility')"
                        :rules="[val => validateField(val)]"
                        color="accent"
                        lazy-rules
                        map-options
                        emit-value
                        outlined
                    />
                    <!-- Benchmark report -->
                    <div v-else>
                        <div class="q-mb-md">
                            <q-radio
                                v-if="showFacilityGroupSelect"
                                v-model="form.selectedFacilitiesOption"
                                @update:model-value="resetFacilitySelection()"
                                val="multiFacility"
                                :label="$t('Facilities')"
                                class="q-mr-xl"
                                color="accent"
                            />
                            <q-radio
                                v-if="
                                    showFacilityGroupSelect &&
                                    facilityGroupsOptions.length > 0
                                "
                                v-model="form.selectedFacilitiesOption"
                                @update:model-value="resetFacilitySelection()"
                                val="facilityGroup"
                                :label="$t('Facility Group')"
                                class="q-mr-xl"
                                color="accent"
                            />
                        </div>

                        <!-- Facilities select-->
                        <q-select
                            v-if="
                                form.selectedFacilitiesOption ===
                                'multiFacility'
                            "
                            id="facility-select"
                            :model-value="form.selectedFacilities"
                            @update:model-value="updateSelectedFacilities"
                            :options="facilityOptions"
                            :label="$t('Select a Facility')"
                            :rules="[val => validateField(val)]"
                            color="accent"
                            lazy-rules
                            map-options
                            clearable
                            multiple
                            emit-value
                            outlined
                        >
                            <template v-slot:option="scope">
                                <q-item
                                    v-bind="scope.itemProps"
                                    v-on="scope.itemEvents || {}"
                                >
                                    <q-item-section>
                                        <div
                                            class="flex items-center justify-between"
                                        >
                                            <div>
                                                {{ scope.opt.label }}
                                            </div>
                                            <q-icon
                                                v-if="scope.selected"
                                                size="20px"
                                                color="grey-5"
                                                name="fa-light fa-circle-xmark"
                                            />
                                        </div>
                                    </q-item-section>
                                </q-item>
                            </template>
                        </q-select>
                        <!-- Facility Group select-->
                        <q-select
                            v-if="
                                form.selectedFacilitiesOption ===
                                'facilityGroup'
                            "
                            id="facility-group-select"
                            :model-value="form.selectedFacilityGroup"
                            @update:model-value="updateSelectedFacilityGroup"
                            :options="facilityGroupsOptions"
                            :label="$t('Select a Facility Group')"
                            :rules="[val => validateField(val)]"
                            color="accent"
                            lazy-rules
                            map-options
                            emit-value
                            outlined
                        />
                    </div>

                    <div
                        v-if="
                            form.report.type &&
                            isFacilitySelected &&
                            form.report.type !== 'benchmark'
                        "
                        class="col-12"
                    >
                        <div class="row">
                            <q-radio
                                v-model="form.selectedAssetsOption"
                                val="allAssets"
                                class="col-6 q-pr-sm"
                                :label="$t('All assets')"
                                :style="
                                    $q.screen.lt.sm ? '' : 'padding-right: 5rem'
                                "
                                @input="emptySelectedAsset"
                                color="accent"
                            />
                            <q-radio
                                id="particular-asset-radio"
                                v-model="form.selectedAssetsOption"
                                val="particularAsset"
                                class="col-6 q-pl-sm"
                                :label="$t('Specific assets')"
                                color="accent"
                                :disable="!isFacilitySelected"
                            >
                                <q-tooltip
                                    :offset="[0, 0]"
                                    v-if="!isFacilitySelected"
                                >
                                    {{
                                        $t(
                                            'Please select a facility to show the list of assets.'
                                        )
                                    }}
                                </q-tooltip>
                            </q-radio>
                        </div>
                        <div
                            class="row q-my-sm w-100"
                            v-if="disableSelectInputStyle && isFacilitySelected"
                        >
                            <remote-select
                                id="asset-select"
                                class="full-width"
                                :model-value="form.selectedAssets"
                                @update:model-value="
                                    form.selectedAssets = $event
                                "
                                :label="$t('Asset')"
                                :baseURL="'assets/'"
                                :urlParams="{
                                    facility: form.selectedFacility,
                                    ordering: 'name'
                                }"
                                outlined
                                searchable
                                clearable
                                emit-value
                                :multiple="true"
                            />
                            <span
                                v-if="form.assetSelectionError"
                                class="errorMessage"
                                style="padding-top: 5px"
                            >
                                {{ form.assetSelectionError }}
                            </span>
                        </div>
                    </div>

                    <q-stepper-navigation>
                        <div class="row justify-end">
                            <q-btn
                                @click="$router.go(-1)"
                                :label="$t('Cancel')"
                                class="q-mr-sm"
                                text-color="primary"
                                color="white"
                                outline
                            />
                            <q-btn
                                @click="step = 2"
                                color="primary"
                                label="Continue"
                                :disable="!validateStep(1)"
                            />
                        </div>
                    </q-stepper-navigation>
                </q-step>

                <!-- File Generation fields  -->
                <q-step
                    :name="2"
                    :title="$t('Configure the report details')"
                    :done="validateStep(2)"
                    :disable="!validateStep(1)"
                >
                    <div class="row q-mt-sm q-mb-sm">
                        <span
                            class="col-12 text-subtitle1 q-mt-sm"
                            style="font-weight: 400"
                        >
                            {{ $t('Generate report for the past') }}
                        </span>

                        <div class="col-6 q-pr-sm q-mt-sm">
                            <q-input
                                v-model="form.report.dateRangeWindow"
                                type="number"
                                :label="$t('Duration')"
                                outlined
                                dense
                            />
                        </div>

                        <div class="col-6 q-pl-sm q-mt-sm">
                            <q-select
                                v-model="form.report.dateRangeUnit"
                                :options="filteredUnitOptions"
                                :label="$t('Time Unit')"
                                outlined
                                dense
                                emit-value
                                map-options
                                :disable="!form.report.dateRangeWindow"
                            />
                        </div>
                    </div>
                    <div
                        class="row q-mt-md q-mb-sm"
                        v-if="
                            form.report.type === 'trends' ||
                            form.report.type === 'kpi_trends' ||
                            form.report.type === 'data_entry_performance'
                        "
                    >
                        <span
                            class="col-12 text-subtitle1 q-mt-sm"
                            style="font-weight: 400"
                        >
                            {{
                                $t(
                                    'Choose how detailed the report data should be (e.g., hourly, daily)'
                                )
                            }}
                        </span>

                        <q-select
                            class="col-12 q-mt-md"
                            id="resolution-select"
                            v-model="form.report.resolution"
                            :options="resolutionOptions"
                            :label="$t('Resolution')"
                            :rules="[val => validateField(val)]"
                            color="accent"
                            lazy-rules
                            map-options
                            emit-value
                            outlined
                        />
                    </div>
                    <div
                        class="q-pb-md"
                        v-if="
                            form.report.type === 'kpi' ||
                            form.report.type === 'kpi_trends' ||
                            form.report.type === 'data_entry_performance' ||
                            form.report.type === 'losses' ||
                            form.report.type === 'maintenance_extended'
                        "
                    >
                        <q-checkbox
                            id="comparison-checkbox"
                            v-model="form.report.comparison"
                            :label="$t('Compare to previous period')"
                            color="accent"
                        />
                        <q-icon
                            name="fa-regular fa-question-circle"
                            size="xs"
                            color="secondary"
                            class="q-ml-sm"
                        >
                            <q-tooltip>
                                {{
                                    $t(
                                        'If selected, the report will compare the current period to the previous period with the same duration.'
                                    )
                                }}
                            </q-tooltip>
                        </q-icon>
                    </div>

                    <div class="row">
                        <span
                            class="col-12 text-subtitle1 q-mt-sm"
                            style="font-weight: 400"
                        >
                            {{
                                $t(
                                    'Pick the file format you would like for the report'
                                )
                            }}
                        </span>

                        <q-select
                            id="format-select"
                            class="col-12 q-mt-sm"
                            v-model="form.generation.format"
                            @clear="form.generation.format = ''"
                            :options="fileFormatOptions"
                            :label="$t('File Format')"
                            :rules="[val => validateField(val)]"
                            color="accent"
                            lazy-rules
                            map-options
                            emit-value
                            outlined
                        />
                    </div>

                    <q-stepper-navigation>
                        <div class="row justify-end">
                            <q-btn
                                @click="step = 1"
                                :label="$t('Back')"
                                class="q-mr-sm"
                                text-color="primary"
                                color="white"
                                outline
                            />
                            <q-btn
                                @click="step = 3"
                                color="primary"
                                label="Continue"
                                :disable="!validateStep(2)"
                            />
                        </div>
                    </q-stepper-navigation>
                </q-step>

                <!-- Generation Frequency fields -->
                <q-step
                    :name="3"
                    :title="$t('How often should the report be generated?')"
                    :done="validateStep(3)"
                    :disable="!validateStep(2)"
                >
                    <div
                        class="row items-center q-col-gutter-sm q-mb-lg q-mt-sm"
                    >
                        <div class="col-12 col-sm-3">
                            {{ $t('Frequency') }}
                        </div>
                        <q-select
                            id="frequency-select"
                            v-model="form.generation.frequency"
                            :options="generationFrequencyOptions"
                            :label="$t('Frequency')"
                            class="col-12 col-sm-9"
                            color="accent"
                            lazy-rules
                            map-options
                            emit-value
                            outlined
                            dense
                        />
                    </div>

                    <div
                        v-if="
                            form.generation.frequency !== null &&
                            form.generation.frequency !== 'daily'
                        "
                        class="row q-mb-lg items-center"
                    >
                        <div
                            v-if="form.generation.frequency === 'yearly'"
                            class="col-12 col-sm-3"
                        >
                            {{ $t('Month') }}
                            <q-icon
                                name="fa-regular fa-question-circle"
                                size="xs"
                                color="secondary"
                                class="q-ml-sm"
                            >
                                <q-tooltip>
                                    {{
                                        $t(
                                            'The report will be generated on the first day of the selected month.'
                                        )
                                    }}
                                </q-tooltip>
                            </q-icon>
                        </div>
                        <div v-else class="col-12 col-sm-3">
                            {{ $t('On day') }}
                        </div>

                        <!-- Weekly frequency fields -->
                        <div
                            v-if="form.generation.frequency === 'weekly'"
                            class="col-12 col-sm-9"
                        >
                            <div class="row">
                                <div
                                    v-for="(
                                        days, key
                                    ) in weeklyChoicesTranslated"
                                    v-bind:key="key"
                                    class="text-center"
                                >
                                    <q-checkbox
                                        :model-value="
                                            form.generation.day == key
                                        "
                                        @update:model-value="
                                            form.generation.day = key
                                        "
                                        color="accent"
                                    />
                                    <p>
                                        {{ days.slice(0, 3) }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Monthly frequency fields-->
                        <div
                            v-else-if="form.generation.frequency === 'monthly'"
                            class="col-12 col-sm-9"
                        >
                            <div class="row items-center">
                                <q-input
                                    id="day-input"
                                    v-model="form.generation.day"
                                    type="number"
                                    class="col-4 q-mr-sm q-pb-none"
                                    :rules="[
                                        val =>
                                            val < 32 ||
                                            $t('Enter a value less than 31.')
                                    ]"
                                    outlined
                                    dense
                                    color="accent"
                                />
                                <span>{{ $t('of the month') }}</span>
                            </div>
                        </div>
                        <div
                            v-if="showDayWarning"
                            class="row justify-end q-pa-sm"
                        >
                            <div class="text-red col-12 col-sm-9">
                                {{
                                    $t(
                                        'You have chosen a day (29th, 30th, or 31st) that does not occur every month. Please consider selecting a day between the 1st and 28th.'
                                    )
                                }}
                            </div>
                        </div>

                        <!-- Yearly frequency fields -->
                        <div
                            v-else-if="form.generation.frequency === 'yearly'"
                            class="col-12 col-sm-9"
                        >
                            <q-select
                                id="month-select"
                                :model-value="
                                    yearlyOptions[form.generation.day - 1]
                                "
                                @update:model-value="
                                    form.generation.day = $event
                                "
                                :options="yearlyOptions"
                                color="accent"
                                class="col-9"
                                lazy-rules
                                map-options
                                emit-value
                                outlined
                                dense
                            />
                        </div>
                    </div>

                    <div class="row q-mb-lg items-center">
                        <div class="col-12 col-sm-3">
                            {{ $t('At this time') }}
                            <q-icon
                                name="fa-regular fa-question-circle"
                                size="xs"
                                color="secondary"
                                class="q-ml-sm"
                            >
                                <q-tooltip>
                                    {{
                                        $t(
                                            'You will receive an email with the report 15 minutes after this scheduled time to allow the data to be processed.'
                                        )
                                    }}
                                </q-tooltip>
                            </q-icon>
                        </div>
                        <div class="col-12 col-sm-9 q-pt-xs">
                            <q-input
                                id="time-input"
                                v-model="form.generation.time"
                                outlined
                                mask="##:##:##"
                                dense
                                @click="
                                    $q.screen.lt.md
                                        ? (timePickerShow = true)
                                        : ''
                                "
                                :rules="[isValidReportTime]"
                                color="accent"
                            >
                                <template v-slot:append>
                                    <q-icon
                                        @click="
                                            () => {
                                                timePickerShow = true;
                                            }
                                        "
                                        name="access_time"
                                        class="cursor-pointer"
                                    ></q-icon>
                                </template>
                            </q-input>
                        </div>
                    </div>

                    <div class="row items-center">
                        <div class="col-12 col-sm-3">
                            {{ $t('In this timezone') }}
                        </div>
                        <div class="col-12 col-sm-9">
                            <!-- Display a third timezone option if the report's timezone is different from the local timezone -->
                            <q-radio
                                v-if="localAndReportTimezoneAreDifferent"
                                v-model="form.timezone"
                                :val="reportOriginalTimezone"
                                :label="reportOriginalTimezone"
                                checked
                                keep-color
                                size="md"
                                color="accent"
                            />
                            <q-radio
                                v-model="form.timezone"
                                :val="localTimezoneName"
                                :label="localTimezoneName"
                                checked
                                keep-color
                                size="md"
                                color="accent"
                            />
                            <q-radio
                                v-model="form.timezone"
                                val="UTC"
                                label="UTC"
                                keep-color
                                size="md"
                                color="accent"
                            />
                        </div>
                    </div>

                    <q-dialog v-model="timePickerShow">
                        <q-card>
                            <time-picker
                                v-model="timePickerValue"
                                :with-seconds="true"
                                minute-interval="15"
                            ></time-picker>
                            <div class="row q-ma-md">
                                <div align="right" class="col">
                                    <q-btn
                                        @click="timePickerShow = false"
                                        :label="$t('Cancel')"
                                        class="q-mr-sm"
                                        color="white"
                                        text-color="primary"
                                        outline
                                    />
                                    <q-btn
                                        @click="handleTimePickerSelect"
                                        :label="$t('Select')"
                                        color="primary"
                                    />
                                </div>
                            </div>
                        </q-card>
                    </q-dialog>

                    <div
                        class="row q-mt-lg q-mb-md"
                        :class="!isEdit ? 'justify-end' : 'justify-between'"
                    >
                        <q-btn
                            v-if="isEdit"
                            @click="handleRemove"
                            :color="'negative'"
                            class="q-px-xs bg-white"
                            icon="fas fa-trash-alt"
                            outline
                        ></q-btn>
                        <div class="float-right">
                            <q-btn
                                :label="$t('Save')"
                                :color="'primary'"
                                type="submit"
                                class="float-right"
                                :disable="!validateForm"
                            />
                            <q-btn
                                @click="step = 2"
                                :label="$t('Back')"
                                text-color="primary"
                                class="float-right q-mr-sm"
                                color="white"
                                outline
                            />
                        </div>
                    </div>
                </q-step>
            </q-form>
        </q-stepper>
    </div>
</template>

<script>
import Banner from '@components/Banner.vue';
import RemoteSelect from '@components/RemoteSelect.vue';
import TimePicker from '@components/TimePicker';
import {
    FILE_FORMAT_CHOICES,
    FREQUENCY_CHOICES,
    REPORT_DATE_RANGE_CHOICES,
    REPORT_TYPE_CHOICES,
    REPORT_TYPE_LEGACY_CHOICES,
    RESOLUTION_CHOICES,
    WEEKLY_CHOICES,
    YEARLY_CHOICES
} from '@models/automaticreports';
import { mapGetters } from 'vuex';
import { mapActions, mapState } from 'pinia';
import { useSettingsStore } from '@stores/settings.js';
import { useSharedStore } from '@stores/shared.js';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'AutomaticReportDetail',
    components: {
        Banner,
        TimePicker,
        RemoteSelect
    },
    props: {
        id: {
            type: [Number, String],
            required: false,
            default: null
        }
    },
    watch: {
        /**
         * If the asset selection option is changed to all assets,
         * then empty the selected asset array.
         */
        'form.selectedAssetsOption': {
            handler(option) {
                if (option === 'allAssets') {
                    this.form.selectedAssets = [];
                }
            }
        },
        /**
         * If the report type is changed, reset the form options.
         */
        'form.report.type': {
            handler() {
                // only do this if the form is not in edit mode
                if (!this.isEdit) {
                    this.resetFields();
                }
            }
        },
        'form.report.dateRangeWindow'(newVal, oldVal) {
            // Reset unit if the current one becomes invalid or if value changed
            if (
                this.form.report.dateRangeUnit &&
                this.form.report.dateRangeUnit.endsWith('s')
            ) {
                this.form.report.dateRangeUnit =
                    this.form.report.dateRangeUnit.slice(0, -1);
            }
            const validatedUnit = this.filteredUnitOptions.find(
                item => item.value === this.form.report.dateRangeUnit
            );
            this.form.report.dateRangeUnit = validatedUnit
                ? validatedUnit.value
                : null;
        }
    },
    data() {
        return {
            YEARLY_CHOICES,
            WEEKLY_CHOICES,
            FREQUENCY_CHOICES,
            RESOLUTION_CHOICES,
            REPORT_TYPE_CHOICES,
            REPORT_TYPE_LEGACY_CHOICES,
            REPORT_DATE_RANGE_CHOICES,
            FILE_FORMAT_CHOICES,

            form: {
                selectedAssetsOption: 'allAssets',
                selectedAssets: [],
                // Non-benchmark report
                selectedFacility: null,
                assetSelectionError: null,
                // Benchmark report
                selectedFacilityGroup: null,
                selectedFacilities: [],
                selectedFacilitiesOption: 'multiFacility',

                // report related fields
                report: {
                    type: null,
                    resolution: null,
                    date_range: null,
                    dateRangeWindow: null,
                    dateRangeUnit: null,
                    comparison: false
                },

                // Type specific options
                options: {
                    group_production_by_product: true
                },

                // generation related fields
                generation: {
                    format: null,
                    frequency: null,
                    day: 1,
                    time: '00:00:00'
                },
                timezone: 'UTC'
            },
            unitLimits: {
                hour: 24, // max 24 hours allowed for the date range
                week: 24, // max 24 weeks allowed for the date range
                day: 31, // max 31 days allowed for the date range
                month: 12, // max 12 months allowed for the date range
                year: 1 // max 1 year allowed for the date range
            },
            // time picker modal
            timePickerShow: false,
            timePickerValue: '00:00:00',

            // stepper
            step: 1,

            // legacy report flag
            legacyReportMessage: this.$t(
                'There is a new trends report format available. You can update to the new format by selecting the "Trends" report type.'
            ),
            // store original timezone
            reportOriginalTimezone: null
        };
    },
    mounted() {
        if (this.isEdit) {
            this.initForm();
        } else {
            // initialize time picker value to user default report time
            // Use user default report time if available, otherwise fallback to midnight
            const reportTime =
                this.user && this.user['report_default_start_time']
                    ? this.user['report_default_start_time']
                    : '00:00:00';
            this.form.generation.time = reportTime;
            this.timePickerValue = reportTime;
        }

        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.$t('Reports'),
                route: {
                    name: 'reports_select'
                }
            },
            {
                label: this.$t('Automatic Reports'),
                route: {
                    name: 'automatic_report_list'
                }
            },
            {
                label: this.isEdit
                    ? history.state.facilityLabel
                    : this.$t('Create Automatic Report')
            }
        ]);
    },
    computed: {
        ...mapGetters({
            facilities: 'facilities/getFacilities',
            getFacility: 'facilities/getFacility',
            getAsset: 'facilities/getAsset',
            getFacilityGroup: 'facilities/getFacilityGroup',
            getFacilityGroups: 'facilities/getFacilityGroups'
        }),
        ...mapState(useSettingsStore, ['isTrackingQualityEvents']),
        ...mapState(useAuthStore, ['user']),
        /**
         * Check if the facility is selected.
         * @return {Boolean} - True if the facility is selected.
         */
        isFacilitySelected() {
            if (this.form.selectedFacility) {
                return true;
            }
            return false;
        },
        /**
         * Whether to show facility group select
         * @return {Boolean}
         */
        showFacilityGroupSelect() {
            return (
                this.isBenchmarkReport && this.facilityGroupsOptions.length > 0
            );
        },
        /**
         * Controls the visibility of the asset selection drop down.
         * @returns {Boolean}
         */
        disableSelectInputStyle() {
            if (this.form.selectedAssetsOption === 'allAssets') {
                return false;
            }
            return true;
        },
        /**
         * Get local timezone name.
         * @return {String}
         */
        localTimezoneName() {
            if (this.facility && !this.isBenchmarkReport) {
                return this.facility.timezone;
            }
            // use browser timezone if facility group is selected or benchmark report
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
        },
        /*
         * Get Facility object
         * @return {Object}
         */
        facility() {
            return this.getFacility(this.form.selectedFacility);
        },
        /**
         * Get Facility Group object
         * @return {Object}
         */
        facilityGroup() {
            return this.getFacilityGroup(this.form.selectedFacilityGroup);
        },
        /**
         * Determine if form is in edit mode.
         * @return {Boolean}
         */
        isEdit() {
            return this.id !== null;
        },
        /**
         * Report type options for q-select.
         * @return {Array}
         */
        typeOptions() {
            const sortOptionsAlphabetically = true;
            return this.mapToSelectOptions(
                this.reportTypesWithLegacy,
                sortOptionsAlphabetically
            );
        },
        /**
         * Report date range options for q-select.
         * @return {Array}
         */
        dateRangeOptions() {
            return this.mapToSelectOptions(this.REPORT_DATE_RANGE_CHOICES);
        },
        /**
         * Report resolution options for q-select.
         * @return {Array}
         */
        resolutionOptions() {
            return this.mapToSelectOptions(this.RESOLUTION_CHOICES);
        },
        /**
         * Report file format options for q-select.
         * @return {Array}
         */
        fileFormatOptions() {
            return this.mapToSelectOptions(this.FILE_FORMAT_CHOICES);
        },
        /**
         * Report generation frequency options for q-select.
         * @return {Array}
         */
        generationFrequencyOptions() {
            return this.mapToSelectOptions(this.FREQUENCY_CHOICES);
        },
        /**
         * Yearly frequency options for q-select.
         * @return {Array}
         */
        yearlyOptions() {
            return this.mapToSelectOptions(this.YEARLY_CHOICES);
        },
        /**
         * Payload for automatic report create/edit endpoints.
         */
        payload() {
            const payload = {
                facility_group: this.form.selectedFacilityGroup,
                facilities: this.form.selectedFacilities,
                frequency: this.form.generation.frequency,
                assets: this.form.selectedAssets || [],
                enabled: true,
                day_in_period: this.form.generation.day,
                timezone: this.form.timezone,
                report_type: this.form.report.type,
                report_format: this.form.generation.format,
                date_range_window: this.form.report.dateRangeWindow,
                date_range_unit: this.form.report.dateRangeUnit,
                time: this.form.generation.time,
                user: this.user.id,
                resolution: this.form.report.resolution,
                comparison: this.form.report.comparison,
                options:
                    this.form.report.type === 'trends'
                        ? this.form.options.group_production_by_product
                            ? { group_production_by_product: true }
                            : this.form.options.group_production_by_resolution
                              ? { group_production_by_resolution: true }
                              : {}
                        : this.form.options
            };

            if (!this.isBenchmarkReport) {
                payload['facility'] = this.form.selectedFacility;
            }

            return payload;
        },
        /**
         * Get facilities.
         * @return {Object[]} - Array of facilities.
         */
        facilityOptions() {
            if (!this.facilities) {
                return [];
            }

            return this.facilities.map(facility => ({
                label: facility.name,
                value: parseInt(facility.id)
            }));
        },
        /**
         * Get selectable facility groups options.
         * @return {Array} - Array of facility groups options.
         */
        facilityGroupsOptions() {
            if (!this.getFacilityGroups) {
                return [];
            }

            // Only show facility groups that the user has access to the facilities in that group
            return this.getFacilityGroups.map(group => ({
                label: group.name,
                value: parseInt(group.id)
            }));
        },
        /**
         * Enable the save button.
         * @return {Boolean}
         */
        validateForm() {
            return (
                this.validateStep(1) &&
                this.validateStep(2) &&
                this.validateStep(3)
            );
        },
        /**
         * Show warning if day is not present in every month.
         * @return {Boolean}
         */
        showDayWarning() {
            return (
                this.form.generation.day > 28 && this.form.generation.day < 32
            );
        },
        /**
         * Get weekly choices translated.
         * @return {Object}
         */
        weeklyChoicesTranslated() {
            const weekDays = Object.keys(this.WEEKLY_CHOICES).reduce(
                (acc, key) => {
                    acc[key] = this.$t(this.WEEKLY_CHOICES[key]);
                    return acc;
                },
                {}
            );
            return weekDays;
        },
        /**
         * Get report types including legacy report if needed.
         * @return {Object}
         */
        reportTypesWithLegacy() {
            let reportTypes = this.isLegacyReport
                ? {
                      ...this.REPORT_TYPE_CHOICES,
                      ...this.REPORT_TYPE_LEGACY_CHOICES
                  }
                : this.REPORT_TYPE_CHOICES;

            // Exclude 'quality' report type if isTrackingQualityEvents is disabled.
            if (!this.isTrackingQualityEvents) {
                reportTypes = Object.fromEntries(
                    Object.entries(reportTypes).filter(
                        ([key]) => key !== 'quality'
                    )
                );
            }

            return reportTypes;
        },
        /**
         * Report type is a legacy option.
         * @return {Boolean} - True if the report type is a legacy option.
         */
        isLegacyReport() {
            return this.form.report.type in this.REPORT_TYPE_LEGACY_CHOICES;
        },
        /**
         * If benchmark report
         * @return {Boolean}
         */
        isBenchmarkReport() {
            return this.form.report.type === 'benchmark';
        },
        /**
         * Filtered unit options based on the input value.
         * @return {Array} - List of valid units.
         */
        filteredUnitOptions() {
            const value = Number(this.form.report.dateRangeWindow);
            if (!value || value <= 0) return Object.keys(this.unitLimits);
            // Filter units where dateRangeWindow is less than or equal to the limit of the units

            const validUnits = Object.entries(this.unitLimits).filter(
                ([unit, limit]) => value <= limit
            );
            // if no valid units, return day/days as default
            if (validUnits.length === 0) {
                return [{ label: this.$t('day', value), value: 'day' }];
            }
            const res = validUnits.map(([unit]) => ({
                label: this.$t(unit, value),
                value: unit
            }));
            return res;
        },
        /**
         * Check if local and report timezone are different.
         * @return {Boolean}
         */
        localAndReportTimezoneAreDifferent() {
            return !this.reportOriginalTimezone ||
                this.reportOriginalTimezone === 'UTC'
                ? false
                : this.localTimezoneName !== this.reportOriginalTimezone;
        }
    },
    methods: {
        ...mapActions(useSharedStore, [
            'setBreadcrumbs',
            'beginLoading',
            'stopLoading'
        ]),
        /**
         * Reset form fields
         */
        resetFields() {
            // reset options
            this.form.options = {};
            if (this.form.report.type === 'phases') {
                this.form.options.downtime_only = false;
                this.form.report.comparison = false;
            } else if (this.form.report.type === 'trends') {
                this.form.options.group_production_by_product = true;
                this.form.options.group_production_by_resolution = false;
                this.form.report.comparison = false;
            }
            // reset selected assets
            this.form.selectedAssets = [];
            this.form.selectedAssetsOption = 'allAssets';
            this.form.assetSelectionError = null;
            // reset facility selection
            this.resetFacilitySelection();
        },
        /**
         * Validate each step in the form.
         * @param {Boolean | String} step - Step number.
         */
        validateStep(step) {
            // ensure the previous steps are validated
            if (step > 1 && !this.validateStep(step - 1)) {
                return false;
            }

            switch (step) {
                case 1: {
                    const isTypeSelected =
                        this.form.report.type !== null &&
                        this.form.report.type !== '';

                    // Check if facility selection is valid depending on report type
                    let hasFacilitySelection = false;

                    if (this.isBenchmarkReport) {
                        if (
                            this.form.selectedFacilitiesOption ===
                            'multiFacility'
                        ) {
                            if (this.facilityOptions.length > 1) {
                                hasFacilitySelection =
                                    this.form.selectedFacilities.length >= 2;
                            } else {
                                hasFacilitySelection =
                                    this.form.selectedFacilities.length === 1;
                            }
                        } else if (
                            this.form.selectedFacilitiesOption ===
                            'facilityGroup'
                        ) {
                            hasFacilitySelection =
                                this.form.selectedFacilityGroup !== null;
                        }
                    } else {
                        hasFacilitySelection =
                            this.form.selectedFacility !== null;
                    }

                    return isTypeSelected && hasFacilitySelection;
                }
                case 2: {
                    // Tested resolution for trends and data entry performance
                    if (
                        this.form.report.dateRangeWindow &&
                        this.form.report.dateRangeUnit &&
                        this.form.generation.format !== null
                    ) {
                        if (
                            this.form.report.type === 'kpi_trends' ||
                            this.form.report.type === 'data_entry_performance'
                        ) {
                            return this.form.report.resolution !== null;
                        }
                        return true;
                    }
                    return false;
                }
                case 3:
                    return (
                        this.form.timezone !== null &&
                        this.form.generation.time !== '' &&
                        this.form.generation.frequency !== null
                    );
                default:
                    return false;
            }
        },
        /**
         * Initialize form.
         */
        initForm() {
            this.form.selectedFacility = history.state.facility;
            this.form.selectedFacilityGroup = history.state.facilityGroup;
            this.form.selectedFacilities = history.state.facilities;
            this.form.timezone = history.state.timezone;
            this.form.report.type = history.state.reportType;
            this.form.report.resolution = history.state.resolution;
            this.form.report.dateRangeWindow = history.state.dateRangeWindow;
            this.form.report.dateRangeUnit = history.state.dateRangeUnit;
            this.form.report.comparison = history.state.comparison;
            this.form.generation.format = history.state.reportFormat;
            this.form.generation.frequency = history.state.frequency;
            this.form.generation.day = history.state.dayInPeriod;
            this.form.generation.time = history.state.time;

            // save original timezone for comparison
            this.reportOriginalTimezone = history.state.timezone;

            // set the select asset option based on the selected assets
            if (history.state.assets && history.state.assets.length > 0) {
                this.form.selectedAssets = history.state.assets;
                this.form.selectedAssetsOption = 'particularAsset';
            }

            // set the facilities option
            if (
                this.form.selectedFacilities &&
                this.form.selectedFacilities.length > 0
            ) {
                this.form.selectedFacilitiesOption = 'multiFacility';
            }
            if (this.form.selectedFacilityGroup !== null) {
                this.form.selectedFacilitiesOption = 'facilityGroup';
            }

            const options = history.state.options;
            if (options) {
                const reportType = this.form.report.type;
                // phases type - validate if the downtime_only option is present
                if (reportType === 'phases' && 'downtime_only' in options) {
                    this.form.options.downtime_only = options.downtime_only;
                }
                if (reportType === 'trends') {
                    if ('group_production_by_product' in options) {
                        this.form.options = {
                            group_production_by_product: true,
                            group_production_by_resolution: false
                        };
                    }
                    if ('group_production_by_resolution' in options) {
                        this.form.options = {
                            group_production_by_product: false,
                            group_production_by_resolution: true
                        };
                    }
                }
            }
        },
        /**
         * Reset all facility selection.
         */
        resetFacilitySelection() {
            this.form.selectedFacility = null;
            this.form.selectedFacilityGroup = null;
            this.form.selectedFacilities = [];
            this.form.selectedAssetsOption = 'allAssets';
        },
        /**
         * Create a new automatic report
         */
        async create() {
            if (
                this.form.selectedAssetsOption === 'particularAsset' &&
                this.form.selectedAssets &&
                this.form.selectedAssets.length === 0
            ) {
                this.form.assetSelectionError = this.$t(
                    'Please select an asset'
                );
            }

            this.beginLoading(`${this.$t('Creating automatic report')}...`);
            try {
                await this.$axios.post('/periodic_reports/', this.payload);
                this.$notifySuccess(this.$t('Automatic report created'));
                this.$router.go(-1);
            } catch (error) {
                console.error(
                    'Failed to create automatic reports due to: ',
                    error
                );
                this.$notifyError(
                    this.$t(
                        'Failed to create automatic reports. Please try again later.'
                    )
                );
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Edit an automatic report
         */
        async update() {
            this.beginLoading(`${this.$t('Updating automatic report')}...`);
            try {
                await this.$axios.put(
                    `/periodic_reports/${this.id}/`,
                    this.payload
                );
                this.$notifySuccess(this.$t('Automatic report updated'));
                this.$router.go(-1);
            } catch (error) {
                console.error(
                    'Failed to update automatic reports due to: ',
                    error
                );
                this.$notifyError(
                    this.$t(
                        'Failed to update automatic reports. Please try again later.'
                    )
                );
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Delete an automatic report
         */
        async remove() {
            this.beginLoading(`${this.$t('Deleting automatic report')}...`);
            try {
                await this.$axios.delete(`/periodic_reports/${this.id}/`);
                this.$notifySuccess(this.$t('Automatic report deleted'));
                this.$router.go(-1);
            } catch (error) {
                console.error(
                    'Failed to delete automatic reports due to: ',
                    error
                );
                this.$notifyError(
                    this.$t(
                        'Failed to delete automatic reports. Please try again later.'
                    )
                );
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Handle remove product.
         */
        async handleRemove() {
            this.$confirmDelete(
                this.$t(
                    'Are you sure you want to delete this automatic report?'
                )
            ).onOk(async () => {
                await this.remove();
            });
        },
        /**
         * Convert choice map to options array for q-select.
         * @param {Object} key-value map
         * @param {Boolean} sort - Sort options by label
         * @return {Array} options in q-select format
         */
        mapToSelectOptions(map, sort = false) {
            const options = Object.keys(map).map(key => {
                return {
                    label: this.$t(map[key]),
                    value: key
                };
            });

            // sort options by label if sort is true
            if (sort) {
                options.sort((a, b) => a.label.localeCompare(b.label));
            }

            return options;
        },
        /*
         * Handle time picker select
         */
        handleTimePickerSelect() {
            this.timePickerShow = false;
            this.form.generation.time = this.timePickerValue;
        },
        /**
         * Update selected facility
         * @param {Number} value
         */
        updateSelectedFacility(value) {
            this.resetFacilitySelection();
            this.form.selectedFacility = value;
            this.form.timezone = this.facility.timezone;
        },
        /**
         * Update selected facilities
         * @param {Array} value
         */
        updateSelectedFacilities(value) {
            this.resetFacilitySelection();
            this.form.selectedFacilities = value || [];
            this.form.timezone = this.localTimezoneName;
        },
        /**
         * Update selected facility group
         * @param {Number} value
         */
        updateSelectedFacilityGroup(value) {
            this.resetFacilitySelection();
            this.form.selectedFacilityGroup = value;
            this.form.timezone = this.localTimezoneName;
        },
        /**
         * Validate form field
         * @param {Boolean | String} value
         */
        validateField(value) {
            if (value !== null) {
                return true;
            }
            return this.$t('This field is required');
        },
        /**
         * Validate the report time for the q-input.
         * This function is used as a rule for the q-input.
         * @return {Boolean | String} Error message or true if validation passes.
         */
        isValidReportTime() {
            const time = this.form.generation.time.split(':');
            const hours = parseInt(time[0]);
            const minutes = parseInt(time[1]);
            const seconds = parseInt(time[2]);

            if (
                hours < 0 ||
                hours > 23 ||
                minutes < 0 ||
                minutes > 59 ||
                seconds < 0 ||
                seconds > 59
            ) {
                return this.$t('Please enter a valid time');
            }

            return true;
        },
        /**
         * Empty selected assets when all assets are selected.
         */
        emptySelectedAsset() {
            this.form.selectedAssets = [];
        },
        /**
         * Handle trends report group by product toggle
         * @param val True if group by product is selected, else False
         */
        onTrendsGroupByProductToggle(val) {
            if (val) {
                this.form.options = {
                    group_production_by_product: true,
                    group_production_by_resolution: false
                };
            }
        },
        /**
         * Handle trends report group by resolution toggle
         * @param val True if group by resolution is selected, else False
         */
        onTrendsGroupByResolutionToggle(val) {
            if (val) {
                this.form.options = {
                    group_production_by_product: false,
                    group_production_by_resolution: true
                };
            }
        }
    }
};
</script>
